import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Printer,
  FileText,
  FileSpreadsheet,
  Mail,
  X,
  Send,
  Loader2,
  Maximize2,
  Check,
} from "lucide-react";

import { cn } from "@/lib/utils";
import {
  PRINT_TEMPLATES,
  PRINT_TEMPLATE_KEY,
  getTemplateConfig,
} from "./PrintTemplate";

import { toast } from "@/hooks/use-toast";
import { PrintView } from "./PrintView";



export default function ExportDialog({
  open,
  onOpenChange,
  data,
  mode, //"print" | "email";
  pageTitle = "PRINT",
  email = { subject: "", body: "" },
  children,
  ExportFileContent, //()=>{}
  heading = "",
}) {
    if(!open) return;
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState(email?.subject || "");
  const [emailBody, setEmailBody] = useState(email?.body || "");
  const [exportFormat, setExportFormat] = useState("pdf"); //<"pdf" | "xls" | "doc">
  const [isSending, setIsSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("classic");
  const [showFullPreview, setShowFullPreview] = useState(false);
  const printRef = useRef(null);
         const MasterCompanyDetails =  JSON.parse(localStorage.getItem("MasterCompanyDetails") || "");
    
  useEffect(() => {
    const saved = localStorage.getItem(PRINT_TEMPLATE_KEY);
    if (saved && PRINT_TEMPLATES.find((t) => t.id === saved)) {
      setSelectedTemplate(saved);
    }
  }, []);
  const templateConfig = getTemplateConfig(selectedTemplate);
  const colors = templateConfig.colors;
  // Save template preference
  useEffect(() => {
    localStorage.setItem(PRINT_TEMPLATE_KEY, selectedTemplate);
  }, [selectedTemplate]);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast({
        title: "Error",
        description: "Unable to open print window. Please allow popups.",
        variant: "destructive",
      });
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${pageTitle}</title>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { 
              font-family: 'Inter', system-ui, sans-serif; 
              color: ${colors.valueColor};
              background: white;
              padding: 24px;
            }
            .print-view { max-width: 800px; margin: 0 auto; }
            .font-bold { font-weight: 700; }
            .font-semibold { font-weight: 600; }
            .font-medium { font-weight: 500; }
            .font-mono { font-family: monospace; }
            .text-xs { font-size: 10px; }
            .text-sm { font-size: 12px; }
            .text-lg { font-size: 16px; }
            .text-xl { font-size: 18px; }
            .text-2xl { font-size: 20px; }
            .uppercase { text-transform: uppercase; }
            .tracking-wide { letter-spacing: 0.05em; }
            .grid { display: grid; }
            .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
            .grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
            .grid-cols-4 { grid-template-columns: repeat(4, 1fr); }
            .gap-4 { gap: 16px; }
            .gap-6 { gap: 24px; }
            .flex { display: flex; }
            .items-center { align-items: center; }
            .items-start { align-items: flex-start; }
            .justify-between { justify-content: space-between; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            .p-2 { padding: 8px; }
            .p-3 { padding: 12px; }
            .p-4 { padding: 16px; }
            .px-3 { padding-left: 12px; padding-right: 12px; }
            .py-2 { padding-top: 8px; padding-bottom: 8px; }
            .pb-4 { padding-bottom: 16px; }
            .pt-4 { padding-top: 16px; }
            .mb-2 { margin-bottom: 8px; }
            .mb-5 { margin-bottom: 20px; }
            .mb-6 { margin-bottom: 24px; }
            .mt-2 { margin-top: 8px; }
            .mt-6 { margin-top: 24px; }
            .mt-8 { margin-top: 32px; }
            .mx-auto { margin-left: auto; margin-right: auto; }
            .rounded-lg { border-radius: 8px; }
            .rounded { border-radius: 4px; }
            .rounded-t { border-radius: 4px 4px 0 0; }
            .rounded-b { border-radius: 0 0 4px 4px; }
            .w-16 { width: 64px; }
            .h-16 { height: 64px; }
            .h-1 { height: 4px; }
            .w-48 { width: 192px; }
            .w-full { width: 100%; }
            .whitespace-pre-line { white-space: pre-line; }
            .italic { font-style: italic; }
            table { border-collapse: collapse; }
            @media print {
              @page { margin: 0.5in; size: letter; }
              body { padding: 0; }
              .print-section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
  //"pdf" | "xls" | "doc"
  const handleExport = (format) => {
    setExportFormat(format);

    if (format === "pdf") {
      handlePrint();
      return;
    }
    if (!ExportFileContent) return;
    const colors = templateConfig.colors;

    // Generate content for download
    const content = ExportFileContent(data, format, colors);
    const blob = new Blob([content], {
      type:
        format === "xls" ? "application/vnd.ms-excel" : "application/msword",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${pageTitle}.${format === "xls" ? "xls" : "doc"}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export Successful",
      description: `File exported as ${format.toUpperCase()} file`,
    });
  };

  const handleSendEmail = async () => {
    if (!emailTo) {
      toast({
        title: "Error",
        description: "Please enter recipient email address",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);

    // Simulate email sending (would connect to backend)
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSending(false);
    toast({
      title: "Email Sent",
      description: `Email Sent sent to ${emailTo}`,
    });
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mode === "print" ? (
                <>
                  <Printer className="h-5 w-5" />
                  Print / Export {heading || ""}
                </>
              ) : (
                <>
                  <Mail className="h-5 w-5" />
                  Email {heading || ""} Confirmation
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            {mode === "email" && (
              <div className="space-y-4 mb-6 p-4 bg-muted/30 rounded-lg border border-border">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>To *</Label>
                    <Input
                      type="email"
                      placeholder="recipient@example.com"
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>CC</Label>
                    <Input
                      type="email"
                      placeholder="cc@example.com"
                      value={emailCc}
                      onChange={(e) => setEmailCc(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Message</Label>
                  <Textarea
                    rows={3}
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Attachment format:
                  </Label>
                  <div className="flex gap-2 mt-2">
                    {["pdf", "xls", "doc"].map((format) => (
                      <Button
                        key={format}
                        size="sm"
                        variant={
                          exportFormat === format ? "default" : "outline"
                        }
                        onClick={() => setExportFormat(format)}
                      >
                        {format.toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Two-column layout for print mode */}
            <div
              className={cn(
                "grid gap-6",
                mode === "print" ? "grid-cols-[320px_1fr]" : "",
              )}
            >
              {/* Left Column - Export Options & Templates */}
              {mode === "print" && (
                <div className="space-y-4">
                  {/* Export Format */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">
                      Export Format
                    </Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExport("pdf")}
                        className="flex-1"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                      {ExportFileContent && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExport("xls")}
                            className="flex-1"
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-1" />
                            Excel
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExport("doc")}
                            className="flex-1"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Word
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Template Selection */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">
                      Template Style
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {PRINT_TEMPLATES.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => setSelectedTemplate(template.id)}
                          className={cn(
                            "p-2.5 rounded-lg border-2 text-left transition-all relative",
                            selectedTemplate === template.id
                              ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                              : "border-border hover:border-muted-foreground/50",
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-7 h-7 rounded-full shrink-0"
                              style={{ background: template.preview }}
                            />
                            <div className="min-w-0">
                              <p className="font-medium text-xs truncate">
                                {template.name}
                              </p>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {template.description}
                              </p>
                            </div>
                          </div>
                          {selectedTemplate === template.id && (
                            <Check className="absolute top-1.5 right-1.5 h-3 w-3 text-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Right Column - Preview */}
              <div className="border border-border rounded-lg overflow-hidden flex flex-col">
                <div className="bg-muted/50 px-4 py-2 border-b border-border flex items-center justify-between shrink-0">
                  <span className="text-sm font-medium">Preview</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowFullPreview(true)}
                    className="h-7 gap-1.5"
                  >
                    <Maximize2 className="h-3.5 w-3.5" />
                    Full Page
                  </Button>
                </div>
                <ScrollArea className="flex-1 h-[400px]">
                  <div className="p-3">
                    <div
                      className="transform origin-top-left bg-white shadow-sm border border-border/50 rounded"
                      style={{ transform: "scale(0.45)", width: "222%" }}
                    >
                      <PrintView ref={printRef} template={selectedTemplate}>
                        {React.Children.map(children, (child) =>
                          React.isValidElement(child)
                            ? React.cloneElement(child, { data, colors ,companyInfo: MasterCompanyDetails?.[0]})
                            : child,
                        )}
                      </PrintView>
                    </div>
                  </div>
                </ScrollArea>
              </div>
            </div>

            {/* Email mode preview */}
            {mode === "email" && (
              <div className="border border-border rounded-lg overflow-hidden mt-4">
                <div className="bg-muted/50 px-4 py-2 border-b border-border flex items-center justify-between">
                  <span className="text-sm font-medium">Preview</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowFullPreview(true)}
                    className="h-7 gap-1.5"
                  >
                    <Maximize2 className="h-3.5 w-3.5" />
                    Full Page
                  </Button>
                </div>
                <ScrollArea className="h-[250px]">
                  <div className="p-3">
                    <div
                      className="transform origin-top-left bg-white shadow-sm border border-border/50 rounded"
                      style={{ transform: "scale(0.4)", width: "250%" }}
                    >
                      <PrintView
                        ref={printRef}
                        data={data}
                        template={selectedTemplate}
                      >
                        {React.Children.map(children, (child) =>
                          React.isValidElement(child)
                            ? React.cloneElement(child, { data, colors })
                            : child,
                        )}
                      </PrintView>
                    </div>
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            {mode === "print" ? (
              <Button onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            ) : (
              <Button onClick={handleSendEmail} disabled={isSending}>
                {isSending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Email
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Page Preview Modal */}
      <Dialog open={showFullPreview} onOpenChange={setShowFullPreview}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b border-border flex-row items-center justify-between space-y-0">
            <div>
              <DialogTitle>Booking Confirmation Preview</DialogTitle>
              <DialogDescription>
                Full page preview of {data?.fileRef}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowFullPreview(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <ScrollArea className="flex-1">
            <div className="p-8 flex justify-center bg-muted/30">
              <div className="bg-white shadow-xl border border-border rounded-lg max-w-4xl w-full">
                <PrintView template={selectedTemplate}>
                  {React.Children.map(children, (child) =>
                    React.isValidElement(child)
                      ? React.cloneElement(child, { data, colors })
                      : child,
                  )}
                </PrintView>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
